<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Users', path: {name: 'admin-users'} }]" :name="fullName" no-title/>
    <b-row>
      <b-col lg="4" md="5">
        <admin-panel second-body with-footer>
          <div class="mt-3 text-center">
            <b-avatar
                variant="primary"
                :src="user.avatar_url"
                :text="avatarLetters"
                size="150"
            />
            <h4 class="card-title mt-3">{{ fullName }}</h4>
            <h6 v-if="user.position" class="card-subtitle">{{ user.position }}</h6>
            <h6 v-if="user.company" class="card-subtitle">
              <b-link :to="{name: 'admin-company', params: {id: user.company.id.toString()}}">{{ user.company.name }}</b-link>
            </h6>
            <b-badge v-if="user.is_active" variant="success">Active User</b-badge>
            <b-badge v-else variant="danger">Inactive User</b-badge>
            <b-badge v-if="user.company && user.company.status.code !== 'active'" class="mt-1" variant="danger">
              Company Status: {{ user.company.status.name }}
            </b-badge>
          </div>
          <template v-slot:second-body>
            <small class="text-muted">Email address</small>
            <h6>{{ user.email }}</h6>
            <template v-if="user.phone">
              <small class="text-muted">Phone</small>
              <h6>{{ user.phone }}</h6>
            </template>
            <template v-if="user.date_birth">
              <small class="text-muted">Date Birth</small>
              <h6>{{ user.date_birth }}</h6>
            </template>
            <small class="text-muted">Registered</small>
            <h6>{{ registeredOn }}</h6>
            <template v-if="!user.is_active">
              <small class="text-muted">Deactivated</small>
              <h6>
                on {{ deactivatedOn }}
                <template v-if="user.deactivated_by">
                  <br>by
                  <b-link :to="{name: 'admin-user', params: {id: user.deactivated_by.id.toString()}}">
                    {{ user.deactivated_by.name }}
                  </b-link>
                </template>
              </h6>
            </template>
          </template>
          <template v-slot:footer>
            <div class="text-center mt-n1">
              <no-permission-wrapper :show="!canUpdateUsers">
                <b-btn v-if="user.is_active" @click.prevent="deactivateUser" variant="danger" class="mt-1" :disabled="!canUpdateUsers">Deactivate User</b-btn>
                <b-btn v-else @click="activateUser" variant="success" class="mt-1" :disabled="!canUpdateUsers">Reactivate User</b-btn>
              </no-permission-wrapper>
              <b-btn @click="sendPasswordReset" variant="warning" class="mt-1">Send Password Reset Link</b-btn>
              <no-permission-wrapper :show="!canLoginAsUser">
                <b-btn @click="showAuthToken" variant="secondary" class="mt-1" :disabled="!canLoginAsUser">Redeem User Auth Token</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canLoginAsUser">
                <b-btn @click="confirmLoginAsUser" variant="primary" class="mt-1" :disabled="!canLoginAsUser">Login as User</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-if="user.role && user.role.code !== 'admin'" :show="canHijackUsers">
                <b-btn @click="confirmHijackUser" variant="primary" class="mt-1" :disabled="!canHijackUsers">Hijack User</b-btn>
              </no-permission-wrapper>
            </div>
          </template>
        </admin-panel>
      </b-col>
      <b-col lg="8" md="7">
        <admin-panel sub-title="Edit User Profile">
          <b-form @submit.prevent="submitForm">
            <b-row>
              <b-col md="6">
                <b-form-group label="First Name">
                  <b-form-input
                      v-model.trim="formData.first_name"
                      type="text"
                      :state="fieldValid('first_name')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('first_name')">
                    {{ formValidation.first_name }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Last Name">
                  <b-form-input
                      v-model.trim="formData.last_name"
                      type="text"
                      :state="fieldValid('last_name')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('last_name')">
                    {{ formValidation.last_name }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <b-form-group label="Login">
                  <b-form-input
                      v-model.trim="formData.login"
                      type="text"
                      :state="fieldValid('login')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('login')">
                    {{ formValidation.login }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Email">
                  <b-form-input
                      v-model.trim="formData.email"
                      type="text"
                      :state="fieldValid('email')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('email')">
                    {{ formValidation.email }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group label="Role">
              <b-form-input
                  v-if="formData.role === 'admin' && !canCreateAdmins"
                  :value="formData.role"
                  disabled
              />
              <b-form-select
                  v-else
                  v-model.trim="formData.role"
                  :options="rolesOptions"
                  :state="fieldValid('role')"
                  :disabled="!canUpdateUsers"
              />
              <b-form-invalid-feedback :state="fieldValid('role')">
                {{ formValidation.role }}
              </b-form-invalid-feedback>
            </b-form-group>
            <transition name="slide">
              <div v-if="formData.role === 'admin'">
                <b-form-group label="Admin Notifications">
                  <div v-for="(nClass, key) in adminNotificationClasses" :key="key">
                    <b-form-checkbox
                        :key="key"
                        v-model="formData.notifications[nClass.class]"
                        :disabled="!canUpdateUsers"
                    >
                      {{ nClass.name }}
                    </b-form-checkbox>
                  </div>
                </b-form-group>
              </div>
              <div v-else>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Company">
                      <b-form-select
                          v-model.trim="formData.company"
                          :options="companies"
                          :state="fieldValid('company')"
                          :disabled="!canUpdateUsers"
                      />
                      <b-form-invalid-feedback :state="fieldValid('company')">
                        {{ formValidation.company }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="User License">
                      <b-form-input
                          v-model.trim="formData.user_license"
                          type="text"
                          :state="fieldValid('user_license')"
                          :disabled="!canUpdateUsers"
                      />
                      <b-form-invalid-feedback :state="fieldValid('user_license')">
                        {{ formValidation.user_license }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>
                <span class="mb-1 d-inline-block font-italic">Leave METRC API Key empty if you don't want to update it</span>
                <b-form-group label="METRC API Key">
                  <b-form-input
                      v-model.trim="formData.metrc_api_key"
                      type="text"
                      :state="fieldValid('metrc_api_key')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('metrc_api_key')">
                    {{ formValidation.metrc_api_key }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </div>
            </transition>
            <span class="mb-1 d-inline-block font-italic">Leave password fields empty if you don't want to update current password</span>
            <b-row>
              <b-col md="6">
                <b-form-group label="New Password">
                  <b-form-input
                      v-model.trim="formData.password"
                      type="text"
                      :state="fieldValid('password')"
                      :disabled="!canUpdateUsers"
                  />
                  <b-form-invalid-feedback :state="fieldValid('password')">
                    {{ formValidation.password }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Confirm New Password">
                  <b-form-input
                      v-model.trim="formData.password_confirmation"
                      type="text"
                      :disabled="!canUpdateUsers"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <no-permission-wrapper :show="!canUpdateUsers">
              <b-button
                  type="submit"
                  variant="primary"
                  :disabled="!canUpdateUsers || formState.processing"
              >
                Update user Profile
                <b-spinner v-if="formState.processing" class="ml-1" small/>
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>
      </b-col>
      <b-col v-if="user.role && user.role.code === 'admin' && currentUserIsSuperAdmin" xs="12">
        <admin-panel sub-title="Admin Access">
          <b-form @submit.prevent="saveAdminAccess">
            <b-form-group label="Is SuperAdmin?">
              <b-form-checkbox
                v-model="adminAccessFormData.is_super_admin"
                :state="fieldValid('is_super_admin', true)"
              >
                Make this admin a super admin with all permissions
              </b-form-checkbox>
              <b-form-invalid-feedback :state="fieldValid('is_super_admin', true)">
                {{ adminAccessFormValidation.is_super_admin }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-row v-if="!adminAccessFormData.is_super_admin">
              <b-col md="6">
                <span class="font-bold">Available Permissions</span>
                <div v-for="(permissionsGroup, key) in availableAdminPermissions" :key="key" class="permission-group my-2">
                  <div
                      class="font-medium text-uppercase d-flex items-center justify-content-between cursor-pointer permission-group-header"
                      @click="() => togglePermissionGroup('available', permissionsGroup.group)"
                  >
                    <span>{{ permissionsGroup.group.replaceAll('_', ' ') }}</span>
                    <b-icon-chevron-up v-if="typeof visiblePermissionGroups.available[permissionsGroup.group] !== 'undefined'"/>
                    <b-icon-chevron-down v-else/>
                  </div>
                  <b-collapse :visible="typeof visiblePermissionGroups.available[permissionsGroup.group] !== 'undefined'">
                    <div
                        v-for="(permission, index) in permissionsGroup.permissions"
                        :key="index"
                        class="pt-2"
                    >
                      <b-input-group>
                        <b-input :value="permission.name" disabled/>
                        <template #append>
                          <b-button variant="success" @click="addAdminPermission(permission)">Add</b-button>
                        </template>
                      </b-input-group>
                    </div>
                  </b-collapse>
                </div>
              </b-col>
              <b-col md="6">
                <span class="font-bold">Assigned Permissions</span>
                <div v-for="(permissionsGroup, key) in assignedAdminPermissions" :key="key" class="permission-group my-2">
                  <div
                      class="font-medium text-uppercase d-flex items-center justify-content-between cursor-pointer permission-group-header"
                      @click="() => togglePermissionGroup('assigned', permissionsGroup.group)"
                  >
                    <span>{{ permissionsGroup.group.replaceAll('_', ' ') }}</span>
                    <b-icon-chevron-up v-if="typeof visiblePermissionGroups.assigned[permissionsGroup.group] !== 'undefined'"/>
                    <b-icon-chevron-down v-else/>
                  </div>
                  <b-collapse :visible="typeof visiblePermissionGroups.assigned[permissionsGroup.group] !== 'undefined'">
                    <div
                        v-for="(permission, index) in permissionsGroup.permissions"
                        :key="index"
                        class="pt-2"
                    >
                      <b-input-group>
                        <b-input :value="permission.name" disabled/>
                        <template #append>
                          <b-button variant="danger" @click="removeAdminPermission(permission)">Remove</b-button>
                        </template>
                      </b-input-group>
                    </div>
                  </b-collapse>
                </div>
              </b-col>
            </b-row>
            <no-permission-wrapper :show="!canUpdateUsers">
              <b-button
                  type="submit"
                  variant="primary"
                  :disabled="adminAccessFormState.processing || !canUpdateUsers"
              >
                Update user Admin Access
                <b-spinner v-if="adminAccessFormState.processing" class="ml-1" small/>
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-user-auth-token-modal :user-id="parseInt(id)" ref="authTokenModal"/>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import {API_ROUTES} from "../api";
import AdminPanel from "../components/AdminPanel";
import {convertFromUtc} from "../helpers";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminUserAuthTokenModal from "../components/AdminUserAuthTokenModal";
import NoPermissionTooltip from "../components/NoPermissionTooltip.vue";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminUser",
  components: {
    NoPermissionWrapper,
    NoPermissionTooltip, AdminUserAuthTokenModal, AdminBreadcrumb, AdminPanel, AdminPreloader},
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      user: {},
      loading: true,
      companies: [],
      formData: {},
      formValidation: {},
      formState: {
        processing: false
      },
      adminPermissions: [],
      adminAccessFormData: {
        is_super_admin: false,
        admin_permissions: [],
      },
      adminAccessFormValidation: {},
      adminAccessFormState: {
        processing: false
      },
      visiblePermissionGroups: {
        available: {},
        assigned: {}
      }
    }
  },
  created() {
    if (parseInt(this.id) === this.$store.state.auth.user.id) {
      this.$router.replace({name: "admin-my-account"});
    }
    this.$api.get(`${API_ROUTES.ADMIN_USERS}/${this.id}`).then(({data}) => {
      this.user = data.user;
    })
    .catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-users"});
    })
    .finally(() => {
      this.loading = false;
    });
    this.$api.get(API_ROUTES.ADMIN_COMPANIES, {params: {sorting: "name_asc"}}).then(({data}) => {
      this.companies = data.companies.map(company => ({
        value: company.id,
        text: company.name
      }));
    });
    this.loadAdminPermissions();
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    fieldValid(field, adminAccess = false) {
      if (adminAccess) {
        return typeof this.adminAccessFormValidation[field] !== "undefined" && this.adminAccessFormValidation[field].length > 0 ? false : null;
      }

      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    loadAdminPermissions() {
      this.$api.get(API_ROUTES.ADMIN_PERMISSIONS).then(({data}) => {
        this.adminPermissions = data.admin_permissions;
      }).catch(({response: {data}}) => {
        setTimeout(() => {
          this.loadAdminPermissions();
        }, 2000);
      });
    },
    submitForm() {
      if (!this.checkPermission('canUpdateUsers')) {
        return;
      }
      this.formState.processing = true;
      this.$api.post(`${API_ROUTES.ADMIN_USERS}/${this.id}`, this.formData).then(({data}) => {
        this.user = data.user;
        this.$bvToast.toast('User profile information has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update user profile', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    activateUser() {
      if (!this.checkPermission('canUpdateUsers')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to recover user's access to GreenLists App", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_USERS}/${this.id}/restore`).then(({data}) => {
            this.user = data.user;
            this.$bvToast.toast('User has been successfully activated', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    deactivateUser() {
      if (!this.checkPermission('canUpdateUsers')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to cancel user's access to GreenLists App", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_USERS}/${this.id}/delete`).then(({data}) => {
            this.user = data.user;
            this.$bvToast.toast('User has been successfully deactivated', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    sendPasswordReset() {
      this.$bvModal.msgBoxConfirm("User will receive email with instructions how to set a new password", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_USERS}/${this.id}/reset-password`).then(({data}) => {
            this.$bvToast.toast('Password reset link has been successfully sent', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    showAuthToken() {
      this.$refs.authTokenModal.open();
    },
    confirmLoginAsUser() {
      if (!this.checkPermission('canLoginAsUser')) {
        return;
      }
      const companyName = this.user.company ? this.user.company.name : 'No Company';
      this.$bvModal.msgBoxConfirm(`You are about to impersonate ${this.fullName} at ${companyName}. To end the session, you will need to logout from the user app and login again as admin. Do you want to continue?`, {
        title: 'Login as User',
        okVariant: 'primary',
        okTitle: 'Login',
        cancelTitle: 'Cancel',
        centered: true
      }).then(value => {
        if (value) {
          this.loginAsUser();
        }
      });
    },
    loginAsUser() {
      if (!this.checkPermission('canLoginAsUser')) {
        return;
      }
      this.loading = true;
      this.$api.get(`${API_ROUTES.ADMIN_USERS}/${this.id}/auth-token`).then(({data}) => {
        if (data.token) {
          // Set token in store
          this.$store.commit('auth/setToken', data.token);

          // Redirect to user app
          window.location.href = `${process.env.MIX_USER_APP_URL}/marketplace`;
        } else {
          this.$bvToast.toast('Failed to get user auth token', {
            title: 'Error!',
            variant: 'danger'
          });
        }
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to get user auth token', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    confirmHijackUser() {
      if (!this.checkPermission('canHijackUsers')) {
        return;
      }
      const companyName = this.user.company ? this.user.company.name : 'No Company';
      this.$bvModal.msgBoxConfirm(`You are about to hijack user ${this.fullName} at ${companyName}. This will copy METRC API key and company to your profile. To end the session, you will need to click on "Release Hijack" in the app. Do you want to continue?`, {
        title: 'Confirm Hijack Operation',
        okVariant: 'primary',
        okTitle: 'Hijack',
        cancelTitle: 'Cancel',
        centered: true
      }).then(value => {
        if (value) {
          this.hijackUser();
        }
      });
    },
    hijackUser() {
      if (!this.checkPermission('canHijackUsers')) {
        return;
      }
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_USERS}/${this.id}/hijack`).then(({data}) => {
        // Redirect to user app
        window.location.href = `${process.env.MIX_USER_APP_URL}/marketplace`;
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to hijack a user', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    addAdminPermission(permission) {
      let permissionId = permission.id,
          index = this.adminAccessFormData.admin_permissions.indexOf(permissionId);
      if (index < 0) {
        this.adminAccessFormData.admin_permissions.push(permissionId);
        if (typeof this.visiblePermissionGroups.assigned[permission.group] === 'undefined') {
          this.$set(this.visiblePermissionGroups.assigned, permission.group, true);
        }
      }
    },
    removeAdminPermission(permission) {
      let permissionId = permission.id,
          index = this.adminAccessFormData.admin_permissions.indexOf(permissionId);
      if (index >= 0) {
        this.adminAccessFormData.admin_permissions.splice(index, 1);
        if (typeof this.visiblePermissionGroups.available[permission.group] === 'undefined') {
          this.$set(this.visiblePermissionGroups.available, permission.group, true);
        }
      }
    },
    saveAdminAccess() {
      if (!this.checkPermission('canUpdateUsers') || !this.currentUserIsSuperAdmin) {
        return;
      }
      this.adminAccessFormState.processing = true;
      this.$api.post(`${API_ROUTES.ADMIN_PERMISSIONS}/${this.id}`, this.adminAccessFormData).then(({data}) => {
        this.user = data.user;
        this.$bvToast.toast('Admin permissions have been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.adminAccessFormValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update admin permissions', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.adminAccessFormValidation = validationErrors;
      }).finally(() => {
        this.adminAccessFormState.processing = false;
      });
    },
    togglePermissionGroup(type, group) {
      if (typeof this.visiblePermissionGroups[type][group] === 'undefined') {
        this.$set(this.visiblePermissionGroups[type], group, true);
      } else {
        this.$delete(this.visiblePermissionGroups[type], group);
      }
    },
  },
  computed: {
    canCreateAdmins() {
      return this.$store.getters['auth/hasAdminPermissions']('admin_create');
    },
    canUpdateUsers() {
      return this.$store.getters['auth/hasAdminPermissions']('user_update');
    },
    canLoginAsUser() {
      return this.$store.getters['auth/hasAdminPermissions']('user_login');
    },
    canHijackUsers() {
      return this.$store.getters['auth/hasAdminPermissions']('user_hijack');
    },
    fullName() {
      if (!this.user.id) {
        return "";
      }
      return this.user.first_name + ' ' + this.user.last_name;
    },
    avatarLetters() {
      if (!this.user.id) {
        return "";
      }
      return this.user.first_name.substring(0, 1) + this.user.last_name.substring(0, 1);
    },
    registeredOn() {
      if (!this.user.id) {
        return "";
      }
      return convertFromUtc(this.user.created_at, 'L');
    },
    deactivatedOn() {
      if (!this.user.deactivated_at) {
        return "";
      }
      return convertFromUtc(this.user.deactivated_at, 'L');
    },
    rolesOptions() {
      let roles = this.$store.state.data.user_roles;
      if (!this.canCreateAdmins) {
        roles = roles.filter(role => role.code !== 'admin');
      }

      return roles.map(role => role.code);
    },
    adminNotificationClasses() {
      return this.$store.state.data.admin_notifications;
    },
    currentUserIsSuperAdmin() {
      return !! this.$store.state.auth.user.is_super_admin;
    },
    availableAdminPermissions() {
      let permissionsByGroup = {};
      for (let i = 0; i < this.adminPermissions.length; i++) {
        let permission = this.adminPermissions[i];
        if (!permission) {
          continue;
        }
        if (this.adminAccessFormData.admin_permissions.indexOf(permission.id) >= 0) {
          continue;
        }
        if (typeof permissionsByGroup[permission.group] === 'undefined') {
          permissionsByGroup[permission.group] = {
            group: permission.group,
            permissions: []
          };
        }
        permissionsByGroup[permission.group].permissions.push(permission);
      }

      return Object.values(permissionsByGroup);
    },
    assignedAdminPermissions() {
      let permissionsByGroup = {};
      for (let i = 0; i < this.adminAccessFormData.admin_permissions.length; i++) {
        let permissionId = this.adminAccessFormData.admin_permissions[i],
            permission = this.adminPermissions.find(p => p.id === permissionId);
        if (!permission) {
          continue;
        }
        if (typeof permissionsByGroup[permission.group] === 'undefined') {
          permissionsByGroup[permission.group] = {
            group: permission.group,
            permissions: []
          };
        }
        permissionsByGroup[permission.group].permissions.push(permission);
      }

      return Object.values(permissionsByGroup);
    }
  },
  watch: {
    user(user) {
      let notifications = {};
      this.adminNotificationClasses.forEach(nClass => {
        notifications[nClass.class] = !(user.disabled_notifications && user.disabled_notifications.indexOf(nClass.class) >= 0);
      });

      this.formData = {
        first_name: user.first_name,
        last_name: user.last_name,
        login: user.login,
        email: user.email,
        role: user.role?.code,
        company: user.company?.id,
        is_active: user.is_active,
        user_license: user.user_license,
        metrc_api_key: '',
        password: '',
        password_confirmation: '',
        notifications
      };

      this.adminAccessFormData = {
        is_super_admin: user.is_super_admin,
        admin_permissions: user.admin_permissions.map(p => p.id),
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.permission-group {
  &-header {
    padding: 5px;
    background: #f8f9fa;
    transition: all .2s ease-in-out;
    color: #212529;

    &:hover {
      background: #dee2e6;
    }
  }
}
</style>
